import { canRemoveAugmentFromGame, isGameOnRoundCard } from './augments';

export function clampBetScore(n) {
  return Math.max(0, Math.min(20, parseInt(n, 10) || 0));
}

export function getRoundBet(round, gameId) {
  return round?.bets?.[String(gameId)] ?? round?.bets?.[gameId];
}

function betHasAugments(b) {
  if (!b) return false;
  if (b.augment) return true;
  if (b.clashAugments && typeof b.clashAugments === 'object' && Object.keys(b.clashAugments).length > 0) {
    return true;
  }
  return false;
}

/** True when the server (or post-save state) has an explicit pick for this game. */
export function hasPlacedBet(round, gameId) {
  const b = getRoundBet(round, gameId);
  if (!b) return false;
  if (b.predictedHomeScore != null && b.predictedAwayScore != null) return true;
  return betHasAugments(b);
}

/** On-card games, plus postponed games that still carry reclaimable augments. */
function gamesInDirtySnapshot(round) {
  if (!round?.games) return [];
  return round.games.filter((game) => {
    if (isGameOnRoundCard(game)) return true;
    return canRemoveAugmentFromGame(game) && betHasAugments(getRoundBet(round, game.gameId));
  });
}

export function roundSnapshotKey(round, leagueCode, roundDataKey) {
  if (round?.partitionKey != null) return `pk:${round.partitionKey}`;
  if (leagueCode != null && leagueCode !== '' && roundDataKey) return `${leagueCode}:${roundDataKey}`;
  return null;
}

export function readSavedSnapshot(savedSnapshots, round, leagueCode, roundDataKey) {
  const pk = round?.partitionKey != null ? `pk:${round.partitionKey}` : null;
  if (pk && savedSnapshots[pk] !== undefined) return savedSnapshots[pk];
  const lk = leagueCode != null && leagueCode !== '' && roundDataKey ? `${leagueCode}:${roundDataKey}` : null;
  if (lk && savedSnapshots[lk] !== undefined) return savedSnapshots[lk];
  return undefined;
}

/** Canonical snapshot of submit payload for dirty-state comparison. */
export function serializeRoundBetsSnapshot(round) {
  if (!round?.games) return '';
  const bets = gamesInDirtySnapshot(round)
    .map((game) => {
      const b = getRoundBet(round, game.gameId);
      const row = {
        gameId: String(game.gameId),
        h: clampBetScore(b?.predictedHomeScore),
        a: clampBetScore(b?.predictedAwayScore)
      };
      if (b?.augment) row.augment = String(b.augment);
      if (b?.bountyPoints != null && Number.isFinite(Number(b.bountyPoints))) {
        row.bv = Number(b.bountyPoints);
      }
      if (b?.clashAugments && typeof b.clashAugments === 'object') {
        const ca = {};
        for (const [k, v] of Object.entries(b.clashAugments).sort(([a], [b]) => a.localeCompare(b))) {
          if (v != null && v !== '') ca[k] = String(v);
        }
        if (Object.keys(ca).length > 0) row.ca = ca;
      }
      return row;
    })
    .sort((x, y) => x.gameId.localeCompare(y.gameId));
  return JSON.stringify({
    ra: round.roundAugment ?? null,
    votes: Array.isArray(round.votes)
      ? [...round.votes].map(String).sort((a, b) => a.localeCompare(b))
      : [],
    bets
  });
}

export function buildSavedSnapshotsFromRoundsData(data, leagueCode) {
  const out = {};
  if (!data) return out;
  for (const key of ['currentRound', 'previousRound', 'nextRound']) {
    const round = data[key];
    if (!round) continue;
    const snapshotKey = roundSnapshotKey(round, leagueCode, key);
    if (snapshotKey) out[snapshotKey] = serializeRoundBetsSnapshot(round);
  }
  return out;
}

export function hasUnsavedRoundChanges(editedRound, savedSnapshots, leagueCode, roundDataKey) {
  if (editedRound == null) return false;
  const savedSnapshot = readSavedSnapshot(savedSnapshots, editedRound, leagueCode, roundDataKey);
  if (savedSnapshot === undefined) return false;
  return savedSnapshot !== serializeRoundBetsSnapshot(editedRound);
}
